import { dictionaries } from '@codametrix/ui-common';
import { api as netApi } from '../../core/net';
import { codeListsOf, isHiddenDiagnosis, isUsableCode } from './stateMappers/transactionsFromProcessInstance';

export type DescriptionRequestRow = {
  code: string;
  codeType: 'CPT' | 'MODIFIER' | 'ICD';
  effectiveDate: string;
  dictionaryName: 'CPT Codes' | 'Modifiers' | 'ICD Codes';
  columnName: 'Code';
};

const DICTIONARY: Record<DescriptionRequestRow['codeType'], DescriptionRequestRow['dictionaryName']> = {
  CPT: 'CPT Codes',
  MODIFIER: 'Modifiers',
  ICD: 'ICD Codes'
};

export function descriptionRequestRows(processInstance: AmplifyAPI.ProcessInstance, effectiveDate: string): DescriptionRequestRow[] {
  const rows: DescriptionRequestRow[] = [];
  const seen = new Set<string>();
  const push = (codeType: DescriptionRequestRow['codeType'], code: unknown) => {
    if (!isUsableCode(code) || seen.has(`${codeType}:${code}`)) return;
    seen.add(`${codeType}:${code}`);
    rows.push({ code, codeType, effectiveDate, dictionaryName: DICTIONARY[codeType], columnName: 'Code' });
  };
  const invoices = Array.isArray(processInstance.invoices) ? processInstance.invoices : [];
  invoices.filter((invoice) => invoice && invoice.deleted !== true).forEach((invoice) => {
    (Array.isArray(invoice.transactions) ? invoice.transactions : [])
      .filter((transaction) => transaction && transaction.deleted !== true)
      .forEach((transaction) => {
        const { procedure, modifiers, diagnoses } = codeListsOf(transaction);
        push('CPT', procedure?.procedure_code);
        modifiers.forEach((modifier) => push('MODIFIER', modifier.modifier_code));
        diagnoses.filter((diagnosis) => !isHiddenDiagnosis(diagnosis)).forEach((diagnosis) => push('ICD', diagnosis.diagnosis_code));
      });
  });
  return rows;
}

export const needsDescriptions = (
  processInstance: AmplifyAPI.ProcessInstance,
  descriptions: CMxAPI.CodeResponse | undefined,
  effectiveDate: string
): boolean => descriptionRequestRows(processInstance, effectiveDate).some((row) => !(row.code in (descriptions ?? {})));

export async function fetchTransactionCodeDescriptions(
  caseRun: CMxAPI.CaseRun,
  processInstance: AmplifyAPI.ProcessInstance
): Promise<CMxAPI.CodeResponse> {
  const rows = descriptionRequestRows(processInstance, String(caseRun.date_of_service ?? ''));
  if (rows.length === 0 || !caseRun.tenant_name) return {};
  const tenant = await netApi<CMxAPI.Organization>({ endpoint: `/organization/by-name/${caseRun.tenant_name}/v1` });
  if (typeof tenant?.tenantId !== 'string') throw new Error('Code descriptions unavailable');
  const descriptions = await netApi<CMxAPI.CodeResponse>({
    endpoint: `/dictionary/codes/description/v2?tenantId=${tenant.tenantId}`,
    init: { method: 'POST' },
    body: rows
  });
  const entries = Object.entries(descriptions && typeof descriptions === 'object' ? descriptions : {})
    .filter(([, entry]) => entry !== null && typeof entry === 'object' && typeof (entry as { code?: unknown }).code === 'string');
  return dictionaries.normalizeDescriptions(Object.fromEntries(entries) as CMxAPI.CodeResponse);
}
